import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { staffApi, trainingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ClipboardCheck, Loader2, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import EvaluationDetail from './EvaluationDetail';
import TemplateEditorDialog from './TemplateEditorDialog';
import StaffCombobox from './StaffCombobox';

const EMPTY_FILTERS = { q: '', store: 'all', evaluator: 'all', level: 'all', status: 'all', min: '', max: '' };

function NewEvaluationDialog({ levels, onClose, onSaved }) {
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    staff_id: '', template_id: '', date: new Date().toISOString().slice(0, 10), period: '',
    evaluator_name: '', level: '',
  });

  useEffect(() => {
    staffApi.list().then(r => setStaff(r.data)).catch(() => setStaff([]));
    trainingApi.listTemplates().then(r => {
      setTemplates(r.data);
      if (r.data[0]) setForm(f => ({ ...f, template_id: f.template_id || r.data[0].template_id }));
    }).catch(() => setTemplates([]));
  }, []);

  const save = async () => {
    if (!form.staff_id) { toast.error('Seleziona il dipendente'); return; }
    if (!form.level) { toast.error('Seleziona il livello'); return; }
    setSaving(true);
    try {
      const r = await trainingApi.createEvaluation({
        staff_id: form.staff_id, template_id: form.template_id || null, date: form.date || null,
        period: form.period || null, evaluator_name: form.evaluator_name || null,
        level: form.level || null,
      });
      toast.success('Scheda creata');
      onSaved(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Creazione fallita');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="new-evaluation-dialog">
        <DialogHeader>
          <DialogTitle>Nuova scheda di valutazione</DialogTitle>
          <DialogDescription>Scegli dipendente, template e livello — i punteggi si compilano dopo la creazione.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Dipendente *</label>
            <StaffCombobox staff={staff} value={form.staff_id}
                           onChange={v => setForm(f => ({ ...f, staff_id: v }))}
                           testId="evaluation-staff-select" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Template</label>
            <Select value={form.template_id} onValueChange={v => setForm(f => ({ ...f, template_id: v }))}>
              <SelectTrigger className="rounded-none mt-1" data-testid="evaluation-template-select">
                <SelectValue placeholder="Seleziona template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.template_id} value={t.template_id}>
                    {t.name} ({t.sections_count} sezioni · {t.items_count} voci)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Livello (mansione) *</label>
            <Select value={form.level} onValueChange={v => setForm(f => ({ ...f, level: v }))}>
              <SelectTrigger className="rounded-none mt-1" data-testid="evaluation-level-select">
                <SelectValue placeholder="Seleziona livello" />
              </SelectTrigger>
              <SelectContent>
                {levels.map(lv => (
                  <SelectItem key={lv} value={lv}>{lv}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Data valutazione</label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                     className="rounded-none mt-1" data-testid="evaluation-date" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Periodo di riferimento</label>
              <Input value={form.period} placeholder="es. 1° semestre 2026"
                     onChange={e => setForm(f => ({ ...f, period: e.target.value }))}
                     className="rounded-none mt-1" data-testid="evaluation-period" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Valutatore (default: tu)</label>
            <Input value={form.evaluator_name} onChange={e => setForm(f => ({ ...f, evaluator_name: e.target.value }))}
                   className="rounded-none mt-1" data-testid="evaluation-evaluator" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="evaluation-create-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null} Crea scheda
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EvaluationsTab({ meta }) {
  const { user } = useAuth();
  const isOps = user?.role === 'operations_manager';
  const [rows, setRows] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [flt, setFlt] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(() => {
    setSelected(new Set());
    trainingApi.listEvaluations()
      .then(r => setRows(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento schede'); setRows([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const uniq = (key) => [...new Set((rows || []).map(r => r[key]).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), 'it'));
  const storeOpts = useMemo(() => uniq('store_name'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const evaluatorOpts = useMemo(() => uniq('evaluator_name'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const levelOpts = useMemo(() => uniq('level'), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => (rows || []).filter(ev => {
    if (flt.q && !(ev.staff_name || '').toLowerCase().includes(flt.q.toLowerCase())) return false;
    if (flt.store !== 'all' && ev.store_name !== flt.store) return false;
    if (flt.evaluator !== 'all' && ev.evaluator_name !== flt.evaluator) return false;
    if (flt.level !== 'all' && ev.level !== flt.level) return false;
    if (flt.status !== 'all' && ev.status !== flt.status) return false;
    const tot = ev.total ?? 0;
    if (flt.min !== '' && tot < (parseInt(flt.min, 10) || 0)) return false;
    if (flt.max !== '' && tot > (parseInt(flt.max, 10) || 0)) return false;
    return true;
  }), [rows, flt]);

  const hasFilters = JSON.stringify(flt) !== JSON.stringify(EMPTY_FILTERS);
  const setF = (k, v) => setFlt(f => ({ ...f, [k]: v }));

  const toggleSel = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allSelected = filtered.length > 0 && filtered.every(ev => selected.has(ev.evaluation_id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(filtered.map(ev => ev.evaluation_id)));

  const bulkDelete = async () => {
    if (!window.confirm(`Eliminare definitivamente ${selected.size} schede selezionate?`)) return;
    setBulkBusy(true);
    try {
      await Promise.all([...selected].map(id => trainingApi.deleteEvaluation(id)));
      toast.success(`${selected.size} schede eliminate`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eliminazione parzialmente fallita');
    } finally {
      setBulkBusy(false);
      load();
    }
  };

  return (
    <div data-testid="evaluations-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <p className="text-sm text-slate-500" data-testid="eval-filtered-count">
          {rows ? (hasFilters ? `${filtered.length} di ${rows.length} schede` : `${rows.length} schede`) : ''}
        </p>
        <div className="flex gap-2">
          {meta.can_edit_template && (
            <Button variant="outline" onClick={() => setShowTemplate(true)} className="gap-1.5" data-testid="edit-template-btn">
              <SlidersHorizontal className="w-4 h-4" /> Modifica template
            </Button>
          )}
          {meta.can_evaluate && (
            <Button onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-evaluation-btn">
              <Plus className="w-4 h-4" /> Nuova scheda
            </Button>
          )}
        </div>
      </div>

      {rows !== null && rows.length > 0 && (
        <div className="bg-white border border-slate-200 px-4 py-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="eval-filters-bar">
          <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mr-1">Filtri</span>
          <Input value={flt.q} onChange={e => setF('q', e.target.value)} placeholder="Cerca dipendente…"
                 className="h-9 w-full sm:h-8 sm:w-44 rounded-none" data-testid="eval-filter-search" />
          <Select value={flt.store} onValueChange={v => setF('store', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-40 rounded-none" data-testid="eval-filter-store"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i negozi</SelectItem>
              {storeOpts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={flt.evaluator} onValueChange={v => setF('evaluator', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-44 rounded-none" data-testid="eval-filter-evaluator"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i valutatori</SelectItem>
              {evaluatorOpts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={flt.level} onValueChange={v => setF('level', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-40 rounded-none" data-testid="eval-filter-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i livelli</SelectItem>
              {levelOpts.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={flt.status} onValueChange={v => setF('status', v)}>
            <SelectTrigger className="h-9 w-full sm:h-8 sm:w-32 rounded-none" data-testid="eval-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="draft">Bozza</SelectItem>
              <SelectItem value="final">Definitiva</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <span className="text-xs text-slate-500">Punteggio</span>
            <Input type="number" min="0" value={flt.min} onChange={e => setF('min', e.target.value)} placeholder="da"
                   className="h-8 w-16 rounded-none" data-testid="eval-filter-score-min" />
            <Input type="number" min="0" value={flt.max} onChange={e => setF('max', e.target.value)} placeholder="a"
                   className="h-8 w-16 rounded-none" data-testid="eval-filter-score-max" />
          </div>
          {hasFilters && (
            <button className="text-xs underline text-slate-500 hover:text-slate-900" onClick={() => setFlt(EMPTY_FILTERS)}
                    data-testid="eval-filters-clear">
              Azzera filtri
            </button>
          )}
        </div>
      )}

      {isOps && selected.size > 0 && (
        <div className="bg-slate-900 text-white px-4 py-2.5 mb-4 flex items-center gap-3 flex-wrap" data-testid="eval-bulk-bar">
          <span className="text-sm">{selected.size} selezionate</span>
          <Button variant="destructive" size="sm" className="h-8 gap-1.5 ml-auto" onClick={bulkDelete}
                  disabled={bulkBusy} data-testid="eval-bulk-delete-btn">
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Elimina selezionate
          </Button>
          <button className="text-xs underline text-slate-300" onClick={() => setSelected(new Set())} data-testid="eval-bulk-clear">
            Deseleziona
          </button>
        </div>
      )}

      {rows === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="evaluations-empty">
          <ClipboardCheck className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Nessuna scheda di valutazione{meta.can_evaluate ? ' — creane una con "Nuova scheda"' : ''}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="evaluations-no-match">
          <p>Nessuna scheda corrisponde ai filtri</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full data-table">
            <thead>
              <tr>
                {isOps && (
                  <th className="w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="eval-select-all" />
                  </th>
                )}
                <th>Dipendente</th><th>Negozio</th><th>Data</th><th>Valutatore</th>
                <th>Punteggio</th><th>Livello</th><th>Stato</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(ev => (
                <tr key={ev.evaluation_id} className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setOpenId(ev.evaluation_id)} data-testid={`evaluation-row-${ev.evaluation_id}`}>
                  {isOps && (
                    <td onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(ev.evaluation_id)}
                             onChange={() => toggleSel(ev.evaluation_id)} data-testid={`eval-select-${ev.evaluation_id}`} />
                    </td>
                  )}
                  <td data-label="Dipendente" className="font-medium text-slate-900">{ev.staff_name}</td>
                  <td data-label="Negozio" className="text-sm text-slate-600">{ev.store_name || '—'}</td>
                  <td data-label="Data" className="text-sm">{ev.date || '—'}</td>
                  <td data-label="Valutatore" className="text-sm text-slate-600">{ev.evaluator_name || '—'}</td>
                  <td data-label="Punteggio" className="text-sm tabular-nums">
                    {ev.total ?? 0}<span className="text-slate-400"> / {ev.max_total ?? '—'}</span>
                  </td>
                  <td data-label="Livello" className="text-sm">
                    {ev.level ? <Badge variant="outline" className="bg-slate-100">{ev.level}</Badge> : '—'}
                  </td>
                  <td data-label="Stato">
                    <Badge variant="outline" className={ev.status === 'final'
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'}>
                      {ev.status === 'final' ? 'Definitiva' : 'Bozza'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showNew && (
        <NewEvaluationDialog levels={meta.evaluation_levels || []} onClose={() => setShowNew(false)}
                             onSaved={(ev) => { setShowNew(false); load(); setOpenId(ev.evaluation_id); }} />
      )}
      {showTemplate && (
        <TemplateEditorDialog onClose={() => setShowTemplate(false)} />
      )}
      {openId && (
        <EvaluationDetail evaluationId={openId} meta={meta}
                          onClose={() => { setOpenId(null); load(); }} />
      )}
    </div>
  );
}
